import type { FloorPlanDocument } from './roomPlanFloorPlan'

const IDENTIFIER_MAX = 200

export type RoomPlanSpatialSearch = Readonly<{
  element?: string
  room?: string
  story?: number
}>

export type ResolvedRoomPlanSpatialTarget = Readonly<{
  fallback: 'element-missing' | 'room-missing' | 'story-missing' | undefined
  selectedId?: string
  selectionMode: 'element' | 'room'
  storyIndex: number
}>

const identifier = (value: unknown): string | undefined => {
  if (typeof value !== 'string' || value.length < 1 || value.length > IDENTIFIER_MAX) {
    return
  }
  return value
}

const story = (value: unknown): number | undefined => {
  const parsed =
    typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : Number.NaN
  return Number.isSafeInteger(parsed) && parsed >= 0 ? parsed : undefined
}

/**
 * Public, platform-neutral query contract for a location inside an immutable
 * RoomPlan scan. The scan id belongs in the route path; only stable manifest
 * ids and a story index belong in search params. Signed artifact URLs never do.
 */
export const decodeRoomPlanSpatialSearch = (
  search: Readonly<Record<string, unknown>>
): RoomPlanSpatialSearch => ({
  element: identifier(search.element),
  room: identifier(search.room),
  story: story(search.story),
})

export const encodeRoomPlanSpatialSearch = (
  target: RoomPlanSpatialSearch
): Readonly<Record<string, string | number>> => ({
  ...(target.element ? { element: target.element } : {}),
  ...(target.room ? { room: target.room } : {}),
  ...(target.story === undefined ? {} : { story: target.story }),
})

const storyForRoom = (document: FloorPlanDocument, roomId: string): number | undefined =>
  document.stories.find((candidate) =>
    candidate.areas.some((area) => area.elementId === roomId || area.roomIds.includes(roomId))
  )?.index ??
  document.stories.find((candidate) =>
    candidate.labels.some((label) => label.elementId === roomId || label.roomId === roomId)
  )?.index

const storyForElement = (document: FloorPlanDocument, elementId: string): number | undefined =>
  document.stories.find(
    (candidate) =>
      candidate.areas.some((area) => area.elementId === elementId) ||
      candidate.linearElements.some((element) => element.elementId === elementId) ||
      candidate.objects.some((object) => object.elementId === elementId) ||
      candidate.labels.some((label) => label.elementId === elementId)
  )?.index

const unresolvedFallback = (
  target: RoomPlanSpatialSearch,
  requestedStory: number | undefined
): ResolvedRoomPlanSpatialTarget['fallback'] => {
  if (target.element) {
    return 'element-missing'
  }
  if (target.room) {
    return 'room-missing'
  }
  return target.story !== undefined && requestedStory === undefined ? 'story-missing' : undefined
}

/** Resolves a URL target against downloaded geometry without trusting it. */
export const resolveRoomPlanSpatialTarget = (
  document: FloorPlanDocument,
  target: RoomPlanSpatialSearch
): ResolvedRoomPlanSpatialTarget | undefined => {
  const firstStory = document.stories[0]?.index
  if (firstStory === undefined) {
    return
  }

  if (target.element) {
    const elementStory = storyForElement(document, target.element)
    if (elementStory !== undefined) {
      return {
        fallback:
          target.story !== undefined && target.story !== elementStory ? 'story-missing' : undefined,
        selectedId: target.element,
        selectionMode: 'element',
        storyIndex: elementStory,
      }
    }
  }

  if (target.room) {
    const roomStory = storyForRoom(document, target.room)
    if (roomStory !== undefined) {
      return {
        fallback: target.element ? 'element-missing' : undefined,
        selectedId: target.room,
        selectionMode: 'room',
        storyIndex: roomStory,
      }
    }
  }

  const requestedStory = document.stories.some((candidate) => candidate.index === target.story)
    ? target.story
    : undefined
  return {
    fallback: unresolvedFallback(target, requestedStory),
    selectionMode: 'room',
    storyIndex: requestedStory ?? firstStory,
  }
}
